// src/pages/AIControlRoomAdaptive/hooks/useTenants.js

import { useState, useEffect } from 'react';
import supabase from '../../../lib/supabaseClient';
import { VISIBLE_TENANTS_OR } from '../../../lib/tenantVisibility';

/**
 * Fetches the tenants shown in the Control Room tenant dropdown.
 *
 * Scoping is role-aware and made explicit in the query rather than left to RLS:
 * - global_admin  → every active tenant (platform-wide overview).
 * - partner_admin → their own tenant plus their child tenants (children link
 *   back via `tenants.parent_id`, set at signup for business_admin children).
 *   Without this, a partner would see every tenant on the platform.
 *
 * business_admin never calls this — the caller gates the dropdown to admins.
 */
export function useTenants({ enabled = true, role, tenantId } = {}) {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!enabled) return;

    const fetchTenants = async () => {
      setIsLoading(true);
      try {
        let query = supabase
          .from('tenants')
          .select('id, name')
          .or('is_test.is.null,is_test.eq.false')
          .or(VISIBLE_TENANTS_OR); // hide inactive/suspended accounts

        // Partners only see their own tenant + their children; global_admin sees all.
        if (role === 'partner_admin' && tenantId) {
          query = query.or(`id.eq.${tenantId},parent_id.eq.${tenantId}`);
        }

        const { data: tenants, error: fetchError } = await query.order('name');

        if (fetchError) throw fetchError;

        setData(tenants || []);
        setError(null);
      } catch (err) {
        console.error('[useTenants] Error:', err.message);
        setError(err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTenants();
  }, [enabled, role, tenantId]);

  return { data, isLoading, error };
}

export default useTenants;